import { Settings } from '@/Settings'
import { dromoFields } from './fields'

class DromoUploadCatalogHelperV2 {
    constructor(imagesInZip) {
        this.imagesInZip = imagesInZip
        this.dromoFields = dromoFields
    }

    static acceptedFileExtensions = ['jpg', 'jpeg', 'png', 'svg', 'gif', 'webp']

    settings() {
        return {
            importIdentifier: 'Products',
            developmentMode: Settings.getConfig('IS_DROMO_DEVELOPMENT_MODE') === 'true',
            reviewStep: {
                enableUserTransformations: true,
            },
            webhookUrl: this.webhookUrl,
            backendSyncMode: 'FULL_DATA',
        }
    }

    fields() {
        return this.dromoFields
    }

    static isValidFileExtension(url) {
        try {
            const urlObj = new URL(url)
            const pathname = urlObj.pathname
            const filename = pathname.split('/').pop()
            if (!filename) {
                return false
            }
            const match = filename.match(/\.([^./?#]+)(?:[?#].*)?$/)

            return match ? DromoUploadCatalogHelperV2.acceptedFileExtensions.includes(match[1].toLowerCase()) : false
        } catch (error) {
            console.error('Invalid URL:', error)
            return false
        }
    }

    static _createResponse(row, message, level) {
        return {
            index: row.index,
            value: row.value,
            info: [{ message, level }],
        }
    }

    _checkImageInZip(row) {
        if (this.imagesInZip?.includes(row.value)) {
            return DromoUploadCatalogHelperV2._createResponse(row, 'Valid filename', 'info')
        }
        return null
    }

    static async _validateImageUrl(row) {
        try {
            const headResponse = await fetch(row.value, { method: 'HEAD' })
            if (!headResponse.ok) {
                return DromoUploadCatalogHelperV2._createResponse(row, 'Invalid image url', 'error')
            }

            const headers = headResponse.headers.get('content-type')
            if (!headers?.includes('image')) {
                return DromoUploadCatalogHelperV2._createResponse(row, 'Invalid image url', 'error')
            }

            return DromoUploadCatalogHelperV2._createResponse(row, 'URL', 'info')
        } catch (error) {
            return DromoUploadCatalogHelperV2._createResponse(row, 'Invalid image url', 'error')
        }
    }

    async validatedImage(row) {
        if (!row) {
            return
        }
        if (!row?.value) {
            return row
        }

        const zipCheck = this._checkImageInZip(row)
        if (zipCheck) {
            return zipCheck
        }

        if (row.value.startsWith('https://') || row.value.startsWith('http://')) {
            const isValidExtension = DromoUploadCatalogHelperV2.isValidFileExtension(row.value)
            if (!isValidExtension) {
                return await DromoUploadCatalogHelperV2._validateImageUrl(row)
            }
            return DromoUploadCatalogHelperV2._createResponse(row, 'URL', 'info')
        }

        return DromoUploadCatalogHelperV2._createResponse(row, 'Invalid filename', 'error')
    }

    async validateImageForColumn(values) {
        const results = await Promise.all(
            values.map(async row => {
                const res = await this.validatedImage(row)
                return res
            }),
        )
        return results
    }

    registerHooks(dromo) {
        dromo.registerColumnHook('Images/1', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/2', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/3', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/4', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/5', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/6', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/7', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/8', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/9', async values => this.validateImageForColumn(values))
        dromo.registerColumnHook('Images/10', async values => this.validateImageForColumn(values))
    }
}

export default DromoUploadCatalogHelperV2
